import { sortExamSections } from "sj-ui-toolkit/src/atlas/utils/examSections";

export interface CategoryItem {
  path: string;
  title?: string;
  description?: string;
  programmingLanguages: string[];
  [key: string]: unknown;
}

/** Sorts categories by the canonical display order (unknown categories go last, in API order). */
export const sortByCustomOrder = (data: CategoryItem[]): CategoryItem[] => sortExamSections(data);

/** Returns true when specific programming languages have been selected. */
export const hasSelectedLanguages = (values: {
  parameters?: { allowedProgrammingLanguages?: string[] };
}): boolean =>
  Array.isArray(values?.parameters?.allowedProgrammingLanguages) &&
  (values.parameters?.allowedProgrammingLanguages?.length ?? 0) > 0;

/** Normalizes a date to midnight local time. Returns null when rawDate is null or undefined. */
export const normalizeToMidnight = (rawDate: Date | string | null | undefined): Date | null => {
  if (!rawDate) return null;
  const date = new Date(rawDate);
  date.setHours(0, 0, 0, 0);
  return date;
};

/** Sets a date to end of day (23:59:59.999). Returns null when rawDate is null or undefined. */
export const setToEndOfDay = (rawDate: Date | string | null | undefined): Date | null => {
  if (!rawDate) return null;
  const date = new Date(rawDate);
  date.setHours(23, 59, 59, 999);
  return date;
};

/** Returns the number of calendar days between two dates (inclusive). */
export const findDaysBetween = (
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined
): number => {
  if (!startDate || !endDate) return 0;
  const start = normalizeToMidnight(startDate);
  const end = normalizeToMidnight(endDate);
  if (!start || !end) return 0;
  if (end < start) return 0;
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;
};

/** Converts a protobuf Duration string (e.g. "3600s") to minutes. */
export const convertDurationToMinutes = (duration: string): number => {
  const seconds = parseInt(duration.replace("s", ""), 10);
  return seconds / 60;
};

interface DueDateLabelOptions {
  isDynamic?: boolean;
  endDate?: Date | string | null;
  days?: number;
}

/** Returns a label/value pair describing the due date for the review step. */
export const getDueDateLabel = ({
  isDynamic,
  endDate,
  days,
}: DueDateLabelOptions): { label: string; value: string } | null => {
  const formattedDate = endDate instanceof Date ? endDate.toString() : (endDate ?? "");

  if (isDynamic) {
    return {
      label: "Dynamic Due Date",
      value: `${days ?? 0} day${days === 1 ? "" : "s"} after enrollment`,
    };
  }
  if (!isDynamic && endDate) {
    return { label: "Fixed End Date", value: formattedDate };
  }
  return null;
};
